import React, { useState, useEffect } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import {
    Container,
    Content2Xl,
    ContentWithVerticalPadding,
} from "components/misc/Layouts";
import tw from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";

// 游戏图片
import BaseballImageSrc from "images/demo/baseballlogo.png";
import RugbyImageSrc from "images/demo/rugbylogo.png";
import FootballImageSrc from "images/demo/footballlogo.png";
import BaseballRightTempSrc from "images/demo/baseballplay.png";
import RugbyRightTempSrc from "images/demo/rugbyplay.png";
import FootballRightTempSrc from "images/demo/footballplay.png";
import FootballgameSrc from "images/demo/footballgame.jpg";
import BaseballgameSrc from "images/demo/baseballgame.jpg";
import rugbygameSrc from "images/demo/rugbygame.jpg";
import tittleSrc from "images/demo/tittleimg.png";

// 基础样式
const SectionContainer = tw(ContentWithVerticalPadding)``;

// 图片容器样式：修改为占满屏幕宽度
const HeadingImageContainer = tw.div`
  w-full 
  mx-auto  // 水平居中
  overflow-hidden  // 防止图片溢出
  mb-6
  relative  // 为绝对定位元素提供参考
`;

// 标题图片样式：修改为占满宽度并保持比例
const HeadingReplacementImage = tw.img`
  w-full  // 宽度占满容器
  h-auto  // 高度自适应，保持原始比例
  object-cover  // 覆盖容器，可能会裁剪图片
  max-h-[300px]  // 限制最大高度，防止过高
`;

// 栏目容器：调整移动端布局
const GameEntry = tw.div`
  bg-white rounded-lg shadow-md overflow-hidden mb-6 
  border-l-4 border-primary-600 
  hover:shadow-xl transition-all duration-300 
  grid grid-cols-12  // 始终使用网格布局
  gap-0
`;

// 左侧图片区域
const GameImageContainer = tw.div`
  col-span-12 sm:col-span-3  // 移动端占满12列，桌面端占3列
  relative 
  overflow-hidden 
  pt-[56.25%]
`;

// 轮播图片容器
const SlideshowContainer = tw.div`
  absolute 
  top-0 
  left-0 
  w-full 
  h-full 
  transition-opacity 
  duration-1000 
  ease-in-out
`;

// 轮播图片样式
const CarouselImage = tw.img`
  w-full 
  h-full 
  object-cover
`;

const GameLeftImage = ({ images, gameTitle }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [images.length]);

    return (
        <>
            {images.map((image, index) => (
                <SlideshowContainer
                    key={index}
                    style={{ opacity: index === currentIndex ? 1 : 0, zIndex: index === currentIndex ? 1 : 0 }}
                >
                    <CarouselImage
                        src={image}
                        alt={`${gameTitle} cover`}
                    />
                </SlideshowContainer>
            ))}
        </>
    );
};

// 左侧logo样式
const GameSmallLogo = tw.img`
  absolute top-0 left-0 
  z-10 
  w-20 h-20 
  sm:w-12 sm:h-12 
  rounded-full 
  bg-white/75 
  p-1 
  shadow-sm 
  object-contain
`;

// 中间信息区域
const GameInfoContainer = tw.div`
  col-span-12 sm:col-span-7  // 移动端占满12列，桌面端占7列
  p-4 sm:p-6
`;
const GameTitle = tw.h2`
  text-xl sm:text-2xl 
  font-bold text-primary-900 mb-2
`;
const GameDesc = tw.p`
  text-gray-600 text-sm sm:text-base 
  leading-relaxed
`;

// 右侧图片容器：移至底部，全宽显示
const DownloadButtonContainer = tw.div`
  col-span-12  // 始终占满12列（位于底部）
  p-2 
  cursor-pointer
  hover:bg-gray-100
  transition-colors
`;

// 下载按钮图片样式
const DownloadButtonImage = tw.img`
  w-full 
  max-w-[400px]  // 增加最大宽度限制
  mx-auto  // 水平居中
  aspect-[6/1]  // 保持6:1比例
  object-contain 
  rounded-md 
  shadow-sm
`;

// 游戏数据
const games = [
    {
        title: "Baseball GOAT",
        description: "Step into the cleats of a rising prospect, perfect your swing and fastball in minor-league grind, and live the drama of climbing from backwater fields to World Series glory.",
        images: [BaseballgameSrc],
        logoImage: BaseballImageSrc,
        rightImage: BaseballRightTempSrc,
        googlePlayLink: "https://play.google.com/store/apps/details?id=com.redfalcon.baseballgoat.android",
        appStoreLink: "https://apps.apple.com/us/app/baseball-goat/id6677019784",
    },
    {
        title: "Rugby GOAT",
        description: "Begin as a rookie in a gritty rugby academy, hone your skills in brutal scrums and lightning-fast tries, and rise to become a legend of the pitch in this hard-hitting sports RPG.",
        images: [rugbygameSrc],
        logoImage: RugbyImageSrc,
        rightImage: RugbyRightTempSrc,
        googlePlayLink: "https://play.google.com/store/apps/details?id=com.redfalcon.rugbygoat.android",
        appStoreLink: "https://apps.apple.com/us/app/rugby-goat/idexample",
    },
    {
        title: "Football GOAT",
        description: "Create your underdog athlete, master the playbook, clash in epic NFL-style showdowns, and carve your path from benchwarmer to Super Bowl MVP in this tactical football adventure.",
        images: [FootballgameSrc],
        logoImage: FootballImageSrc,
        rightImage: FootballRightTempSrc,
        googlePlayLink: "https://play.google.com/store/apps/details?id=com.babuyo.amf.android.oversea",
        appStoreLink: "https://apps.apple.com/us/app/football-goat/id6450971694",
    },
];

// 设备检测和跳转逻辑
function getDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('android')) return 'android';
    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) return 'ios';
    return 'other';
}

export default () => {
    const [deviceType, setDeviceType] = useState('other');

    useEffect(() => {
        setDeviceType(getDeviceType());
    }, []);

    const handleGameClick = (game) => {
        const link = deviceType === 'android' ? game.googlePlayLink :
            deviceType === 'ios' ? game.appStoreLink :
                game.googlePlayLink;
        if (link && link !== '#') window.open(link, '_blank');
    };

    return (
        <AnimationRevealPage disabled>
            <Header />
            <Container tw="bg-gray-100 -mx-4 sm:-mx-8 -mt-8 pt-8 px-4 sm:px-8">
                <Content2Xl>
                    <SectionContainer>

                        {/* 标题图片区域 */}
                        <HeadingImageContainer>
                            <HeadingReplacementImage
                                src={tittleSrc}
                                alt="Sports games showcase"
                            />
                        </HeadingImageContainer>

                        {games.map((game, index) => (
                            <GameEntry key={index}>
                                {/* 左侧图片区域 */}
                                <GameImageContainer>
                                    <GameLeftImage images={game.images} gameTitle={game.title} />
                                    <GameSmallLogo
                                        src={game.logoImage}
                                        alt={`${game.title} logo`}
                                    />
                                </GameImageContainer>

                                {/* 中间信息区域 */}
                                <GameInfoContainer>
                                    <GameTitle>{game.title}</GameTitle>
                                    <GameDesc>{game.description}</GameDesc>
                                </GameInfoContainer>

                                {/* 底部下载按钮区域 */}
                                <DownloadButtonContainer onClick={() => handleGameClick(game)}>
                                    <DownloadButtonImage
                                        src={game.rightImage}
                                        alt={`Download ${game.title}`}
                                    />
                                </DownloadButtonContainer>
                            </GameEntry>
                        ))}
                    </SectionContainer>
                    <div className="App">
                        <Footer />
                    </div>
                </Content2Xl>
            </Container>
        </AnimationRevealPage>
    );
};